<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import ImageUploadModal from '@/components/pageContent/modules/ImageUploadModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';

export default {
  name: 'ImagesWithStripes',
  components: {
    PencilIcon,
    ImageUploadModal,
    EditButtonModal,
  },
  props: {
    leftImage: {
      type: String,
      default: '',
    },
    leftImageMobile: {
      type: String,
      default: '',
    },
    rightImage: {
      type: String,
      default: '',
    },
    rightImageMobile: {
      type: String,
      default: '',
    },
    leftStripes: {
      type: Array,
      default: () => [
        {
          id: 1, text: '', color: '#293241', colorText: '#ECC486',
        },
        {
          id: 2, text: 'place an order online', color: '#ECC486', colorText: '#293241',
        },
      ],
    },
    rightStripes: {
      type: Array,
      default: () => [
        {
          id: 1, text: '', color: '#D7E8F0', colorText: '#5F8AA4',
        },
        {
          id: 2, text: 'talk to our team', color: '#5F8AA4', colorText: '#fff',
        },
      ],
    },
    isPod: {
      type: Boolean,
      default: false,
    },
    isBlogsBlock: {
      type: Boolean,
      default: false,
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update'],
  data() {
    return {
      isLeftImageModalVisible: false,
      isRightImageModalVisible: false,
      isLeftStripeModalVisible: false,
      isRightStripeModalVisible: false,
      editingStripeIndex: null,
      editingStripeSide: null,
    };
  },
  computed: {
    displayLeftImage() {
      if (this.isMobileView && this.leftImageMobile) {
        return this.leftImageMobile;
      }
      return this.leftImage;
    },
    displayRightImage() {
      if (this.isMobileView && this.rightImageMobile) {
        return this.rightImageMobile;
      }
      return this.rightImage;
    },
    editingStripe() {
      if (this.editingStripeIndex === null || !this.editingStripeSide) return null;
      const stripes = this.editingStripeSide === 'left' ? this.leftStripes : this.rightStripes;
      return stripes[this.editingStripeIndex] || null;
    },
  },
  methods: {
    b: bem('images-with-stripes'),
    getStripeText(stripe) {
      if (this.isMobileView && stripe.textMob) {
        return stripe.textMob;
      }
      return stripe.text || '';
    },
    openLeftImageModal() {
      if (this.isEditMode) {
        this.isLeftImageModalVisible = true;
      }
    },
    openRightImageModal() {
      if (this.isEditMode) {
        this.isRightImageModalVisible = true;
      }
    },
    handleLeftImageSave(values) {
      this.$emit('update', {
        leftImage: values.image,
        leftImageMobile: values.imageMobile,
      });
      this.isLeftImageModalVisible = false;
    },
    handleLeftImageDelete() {
      this.$emit('update', {
        leftImage: '',
        leftImageMobile: '',
      });
      this.isLeftImageModalVisible = false;
    },
    handleRightImageSave(values) {
      this.$emit('update', {
        rightImage: values.image,
        rightImageMobile: values.imageMobile,
      });
      this.isRightImageModalVisible = false;
    },
    handleRightImageDelete() {
      this.$emit('update', {
        rightImage: '',
        rightImageMobile: '',
      });
      this.isRightImageModalVisible = false;
    },
    openStripeModal(side, index) {
      if (!this.isEditMode) return;
      this.editingStripeSide = side;
      this.editingStripeIndex = index;
      if (side === 'left') {
        this.isLeftStripeModalVisible = true;
      } else {
        this.isRightStripeModalVisible = true;
      }
    },
    closeStripeModal() {
      this.isLeftStripeModalVisible = false;
      this.isRightStripeModalVisible = false;
      this.editingStripeIndex = null;
      this.editingStripeSide = null;
    },
    handleStripeSave(values) {
      if (this.editingStripeIndex === null || !this.editingStripeSide) return;
      const stripesKey = this.editingStripeSide === 'left' ? 'leftStripes' : 'rightStripes';
      const stripes = this.editingStripeSide === 'left' ? [...this.leftStripes] : [...this.rightStripes];
      stripes[this.editingStripeIndex] = {
        ...stripes[this.editingStripeIndex],
        text: values.buttonText,
        textMob: values.buttonTextMobile,
        link: values.buttonLink,
      };
      this.$emit('update', { [stripesKey]: stripes });
      this.closeStripeModal();
    },
    handleStripeClick(side, index) {
      if (this.isEditMode) {
        this.openStripeModal(side, index);
      }
    },
  },
};
</script>

<template>
  <div :class="b({ pod: isPod, isMobileView, blogsBlock: isBlogsBlock })">
    <button
      v-for="(stripe, index) in leftStripes"
      :key="`left-${stripe.id}`"
      :class="b('stripe', {
        left: true,
        first: index === 0,
        second: index === 1,
        pod: isPod,
        blogsBlock: isBlogsBlock,
        editable: isEditMode && index === 1,
        isMobileView,
      })"
      :style="{
        backgroundColor: stripe.color,
        color: stripe.colorText,
        zIndex: index + 1,
      }"
      type="button"
      @click="handleStripeClick('left', index)"
    >
      <span :class="b('stripe-text', { pod: isPod })">
        {{ getStripeText(stripe) }}
      </span>
      <PencilIcon
        v-if="isEditMode && index === 1"
        :size="14"
        :color="stripe.colorText"
        :class="b('stripe-edit-icon')"
      />
    </button>

    <!-- Right stripes -->
    <button
      v-for="(stripe, index) in rightStripes"
      :key="`right-${stripe.id}`"
      :class="b('stripe', {
        right: true,
        first: index === 0,
        second: index === 1,
        pod: isPod,
        blogsBlock: isBlogsBlock,
        editable: isEditMode && index === 1,
        isMobileView,
      })"
      :style="{
        backgroundColor: stripe.color,
        color: stripe.colorText,
        zIndex: index + 1,
      }"
      type="button"
      @click="handleStripeClick('right', index)"
    >
      <span :class="b('stripe-text', { pod: isPod })">
        {{ getStripeText(stripe) }}
      </span>
      <PencilIcon
        v-if="isEditMode && index === 1"
        :size="14"
        :color="stripe.colorText"
        :class="b('stripe-edit-icon')"
      />
    </button>

    <!-- Left image -->
    <div
      :class="b('image-item', { editable: isEditMode, isMobileView })"
      :style="displayLeftImage ? {
        backgroundImage: `url(${displayLeftImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}"
      role="button"
      tabindex="0"
      @click="openLeftImageModal"
      @keydown.enter="openLeftImageModal"
    >
      <span v-if="!displayLeftImage && isEditMode" :class="b('placeholder-text')">
        <span :class="b('placeholder-plus')">+</span><br>CLICK TO UPLOAD
      </span>
    </div>

    <!-- Right image -->
    <div
      :class="b('image-item', { editable: isEditMode, isMobileView })"
      :style="displayRightImage ? {
        backgroundImage: `url(${displayRightImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      } : {}"
      role="button"
      tabindex="0"
      @click="openRightImageModal"
      @keydown.enter="openRightImageModal"
    >
      <span v-if="!displayRightImage && isEditMode" :class="b('placeholder-text')">
        <span :class="b('placeholder-plus')">+</span><br>CLICK TO UPLOAD
      </span>
    </div>

    <!-- Modals -->
    <ImageUploadModal
      :visible="isLeftImageModalVisible"
      :image="leftImage"
      :image-mobile="leftImageMobile"
      :show-delete="!!leftImage"
      modal-title="Edit Left Image"
      @close="isLeftImageModalVisible = false"
      @save="handleLeftImageSave"
      @delete="handleLeftImageDelete"
    />

    <ImageUploadModal
      :visible="isRightImageModalVisible"
      :image="rightImage"
      :image-mobile="rightImageMobile"
      :show-delete="!!rightImage"
      modal-title="Edit Right Image"
      @close="isRightImageModalVisible = false"
      @save="handleRightImageSave"
      @delete="handleRightImageDelete"
    />

    <EditButtonModal
      :visible="isLeftStripeModalVisible"
      :button-text="editingStripe?.text || ''"
      :button-text-mobile="editingStripe?.textMob || editingStripe?.text || ''"
      :button-link="editingStripe?.link || ''"
      @close="closeStripeModal"
      @save="handleStripeSave"
    />

    <EditButtonModal
      :visible="isRightStripeModalVisible"
      :button-text="editingStripe?.text || ''"
      :button-text-mobile="editingStripe?.textMob || editingStripe?.text || ''"
      :button-link="editingStripe?.link || ''"
      @close="closeStripeModal"
      @save="handleStripeSave"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.images-with-stripes {
  & * {
    font-family: Montserrat, sans-serif;
  }

  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  position: relative;

  @include mediaMinMax(1025, $normal) {
    gap: vw-full(40);
  }

  @include media($small) {
    flex-direction: column;
    gap: vw-mobile(56, 412);
  }

  &_isMobileView {
    flex-direction: column;
    gap: 56px;
  }

  &__image-item {
    width: 620px;
    height: 348px;
    border-radius: 36px;
    background: #d9d9d9;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: filter 0.2s ease;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(620);
      height: vw-full(348);
      border-radius: vw-full(36);
    }

    @include media($small) {
      width: vw-mobile(412, 412);
      height: vw-mobile(269, 412);
      border-radius: vw-mobile(20, 412) 0;
    }

    &_isMobileView {
      width: 412px;
      height: 269px;
      border-radius: 20px 0;
    }

    &_editable:hover {
      filter: brightness(0.9);
    }
  }

  // BlogsBlock variant - different image sizes
  &_blogsBlock &__image-item {
    width: 746px;
    height: 348px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(746);
      height: vw-full(348);
    }

    @include media($small) {
      width: vw-mobile(412, 412);
      height: vw-mobile(260, 412);
      border-radius: initial;
    }

    &_isMobileView {
      width: 412px;
      height: 260px;
      border-radius: initial;
    }
  }

  &__placeholder-text {
    font-family: Montserrat, sans-serif;
    font-size: 24px;
    font-weight: 400;
    color: #7f7f7f;
    text-align: center;
    line-height: normal;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(24);
    }
  }

  &__placeholder-plus {
    font-size: 56px;
    line-height: 1;
    font-weight: 200;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(56);
    }
  }

  &__stripe {
    border: none;
    outline: none;
    position: absolute;
    bottom: 0;
    display: flex;
    align-items: center;
    font-family: Montserrat, sans-serif;
    font-size: 22px;
    font-weight: 600;
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(22);
    }

    @include media($small) {
      font-size: vw-mobile(20, 412);
    }

    &_isMobileView {
      font-size: 20px;
    }

    &_first {
      height: 83px;
      width: calc(100vw / 2 - 379px);
      bottom: 0;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(83);
        width: calc(100vw / 2 - vw-full(379));
      }
    }

    &_first#{&}_isMobileView {
      height: 64px;
      width: 203px;
    }

    &_first#{&}_left#{&}_isMobileView {
      top: 210px;
    }

    &_first#{&}_right#{&}_isMobileView {
      bottom: -6px;
    }

    &_second {
      height: 64px;
      width: calc(100vw / 2 - 153px);
      bottom: -32px;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(64);
        width: calc(100vw / 2 - vw-full(153));
        bottom: vw-full(-32);
      }

      &::after,
      &::before {
        content: '';
        display: block;
        position: absolute;
        top: 50%;
        width: 14px;
        height: 14px;
        border: solid currentColor;
        border-width: 0 3px 3px 0;
        padding: 3px;
        background: transparent;
        pointer-events: none;
        box-sizing: border-box;
        border-color: currentColor !important;

        @include mediaMinMax(1025, $normal) {
          width: vw-full(14);
          height: vw-full(14);
          padding: vw-full(3);
          border-width: 0 vw-full(3) vw-full(3) 0;
        }

        @include media($small) {
          width: vw-mobile(10, 412);
          height: vw-mobile(10, 412);
        }
      }

      &::after {
        transform: translateY(-50%) rotate(-45deg);
      }

      &::before {
        transform: translateY(-50%) translateX(12px) rotate(-45deg);

        @include mediaMinMax(1025, $normal) {
          transform: translateY(-50%) translateX(#{vw-full(12)}) rotate(-45deg);
        }
      }
    }

    &_second#{&}_isMobileView {
      width: 374px;
      height: 52px;
      box-shadow: 3px 3px 6px 0 rgba(95, 138, 164, 0.20);

      &::after,
      &::before {
        width: 10px;
        height: 10px;
        padding: 2px;
        border-width: 0 2px 2px 0;
      }
    }

    &_second#{&}_left#{&}_isMobileView {
      top: 240px;

      &::before {
        transform: translateY(-50%) translateX(8px) rotate(-45deg);
      }
    }

    &_second#{&}_right#{&}_isMobileView {
      bottom: -24px;

      &::before {
        transform: translateY(-50%) translateX(8px) rotate(135deg);
      }
    }

    &_left {
      left: 0;
      border-radius: 0 36px 36px 0;
      justify-content: flex-end;
      padding-right: 90px;

      @include mediaMinMax(1025, $normal) {
        border-radius: 0 vw-full(36) vw-full(36) 0;
        padding-right: vw-full(90);
      }

      @include media($small) {
        top: vw-mobile(213, 412);
        border-radius: 0 vw-mobile(26, 412) vw-mobile(26, 412) 0;
        padding-right: vw-mobile(60, 412);
      }

      &::after,
      &::before {
        right: 60px;

        @include mediaMinMax(1025, $normal) {
          right: vw-full(60);
        }

        @include media($small) {
          right: vw-mobile(40, 412);
        }
      }
    }

    &_left#{&}_isMobileView {
      border-radius: 0 26px 26px 0;
      padding-right: 60px;

      &::after,
      &::before {
        right: 40px;
      }
    }

    &_right {
      right: 0;
      border-radius: 36px 0 0 36px;
      justify-content: flex-start;
      padding-left: 90px;

      @include mediaMinMax(1025, $normal) {
        border-radius: vw-full(36) 0 0 vw-full(36);
        padding-left: vw-full(90);
      }

      @include media($small) {
        bottom: 0;
        border-radius: vw-mobile(26, 412) 0 0 vw-mobile(26, 412);
        padding-left: vw-mobile(60, 412);
      }

      &::after,
      &::before {
        left: 50px;

        @include mediaMinMax(1025, $normal) {
          left: vw-full(50);
        }

        @include media($small) {
          left: vw-mobile(30, 412);
        }
      }

      &::after {
        transform: translateY(-50%) rotate(135deg);
      }

      &::before {
        transform: translateY(-50%) translateX(12px) rotate(135deg);

        @include mediaMinMax(1025, $normal) {
          transform: translateY(-50%) translateX(#{vw-full(12)}) rotate(135deg);
        }
      }
    }

    &_right#{&}_isMobileView {
      border-radius: 26px 0 0 26px;
      padding-left: 60px;

      &::after,
      &::before {
        left: 30px;
      }

      &::after {
        transform: translateY(-50%) rotate(135deg);
      }

      &::before {
        transform: translateY(-50%) translateX(8px) rotate(135deg);
      }
    }

    &_editable:hover {
      opacity: 0.9;
    }

    // Pod variant styles
    &_pod {
      color: #fff !important;
    }

    &_pod#{&}_first {
      height: 40px;
      bottom: 20px;
      width: calc(50% - 208px);
      border-radius: 40px;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(40);
        bottom: vw-full(20);
        width: calc(50% - vw-full(208));
        border-radius: vw-full(40);
      }

      @include media($small) {
        height: vw-mobile(40);
        bottom: vw-mobile(20);
        width: calc(50%);
      }
    }

    &_pod#{&}_second {
      height: 64px;
      width: calc(50% - 128px);
      bottom: -28px;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(64);
        width: calc(50% - #{vw-full(128)});
        bottom: vw-full(-28);
      }

      @include media($small) {
        height: vw-mobile(64);
        bottom: vw-mobile(-8);
      }
    }

    &_pod#{&}_left {
      padding-right: 170px;

      @include mediaMinMax(1025, $normal) {
        padding-right: vw-full(170);
      }

      @include media($small) {
        padding-right: vw-mobile(80, 412);
      }

      &::after,
      &::before {
        right: 120px;

        @include mediaMinMax(1025, $normal) {
          right: vw-full(120);
        }

        @include media($small) {
          right: vw-mobile(40, 412);
        }
      }
    }

    &_pod#{&}_right {
      padding-left: 170px;

      @include mediaMinMax(1025, $normal) {
        padding-left: vw-full(170);
      }

      @include media($small) {
        padding-left: vw-mobile(80, 412);
      }

      &::after,
      &::before {
        left: 120px;

        @include mediaMinMax(1025, $normal) {
          left: vw-full(120);
        }

        @include media($small) {
          left: vw-mobile(40, 412);
        }
      }
    }

    &_pod#{&}_left#{&}_first {
      border-radius: 0 40px 40px 0;

      @include mediaMinMax(1025, $normal) {
        border-radius: 0 vw-full(40) vw-full(40) 0;
      }
    }

    &_pod#{&}_right#{&}_first {
      border-radius: 40px 0 0 40px;

      @include mediaMinMax(1025, $normal) {
        border-radius: vw-full(40) 0 0 vw-full(40);
      }
    }

    &_pod#{&}_left#{&}_second {
      border-radius: 0 36px 36px 0;

      @include mediaMinMax(1025, $normal) {
        border-radius: 0 vw-full(36) vw-full(36) 0;
      }
    }

    &_pod#{&}_right#{&}_second {
      border-radius: 36px 0 0 36px;

      @include mediaMinMax(1025, $normal) {
        border-radius: vw-full(36) 0 0 vw-full(36);
      }
    }

    // BlogsBlock variant - different mobile styles
    &_blogsBlock#{&}_first {
      height: 40px;
      width: calc(100vw / 2 - 183px);
      bottom: 21px;
      border-radius: 0 40px 40px 0;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(40);
        width: calc(100vw / 2 - vw-full(183));
        bottom: vw-full(21);
      }

      @include media($small) {
        display: none;
      }
    }

    &_blogsBlock#{&}_first#{&}_isMobileView {
      display: none;
    }

    &_blogsBlock#{&}_second {
      height: 64px;
      width: calc(100vw / 2 - 129px);
      bottom: -32px;

      @include mediaMinMax(1025, $normal) {
        height: vw-full(64);
        width: calc(100vw / 2 - vw-full(129));
        bottom: vw-full(-32);
      }

      @include media($small) {
        display: flex;
        justify-content: center;
        align-items: center;
        width: vw-mobile(332, 412);
        height: vw-mobile(52, 412);
        border-radius: vw-mobile(26);
        box-shadow: none;
        font-size: vw-mobile(18);
        bottom: vw-mobile(-26);
        transform: none;
      }
    }

    &_blogsBlock#{&}_second#{&}_isMobileView {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 332px;
      height: 52px;
      border-radius: 26px;
      box-shadow: none;
      font-size: 18px;
      bottom: -26px;
      transform: none;
    }

    &_blogsBlock#{&}_left {
      border-radius: 0 40px 40px 0;

      @include media($small) {
        top: vw-mobile(213, 412);
        border-radius: 0 vw-mobile(26, 412) vw-mobile(26, 412) 0;
        padding-right: 0;
      }
    }

    &_blogsBlock#{&}_left#{&}_isMobileView {
      top: 213px;
      border-radius: 0 26px 26px 0;
      padding-right: 0;
    }

    &_blogsBlock#{&}_right {
      border-radius: 40px 0 0 40px;

      @include media($small) {
        bottom: 0;
        border-radius: vw-mobile(26, 412) 0 0 vw-mobile(26, 412);
        padding-left: vw-mobile(60, 412);
      }
    }

    &_blogsBlock#{&}_right#{&}_isMobileView {
      bottom: 0;
      border-radius: 26px 0 0 26px;
      padding-left: 60px;
    }

    &_blogsBlock#{&}_second::after,
    &_blogsBlock#{&}_second::before {
      @include media($small) {
        display: none;
      }
    }

    &_blogsBlock#{&}_second#{&}_isMobileView::after,
    &_blogsBlock#{&}_second#{&}_isMobileView::before {
      display: none;
    }

    &_blogsBlock#{&}_second#{&}_left#{&}_isMobileView {
      top: 234px;
      bottom: auto;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
      border-radius: 26px;
      padding-left: 0;
      padding-right: 0;
      justify-content: center;
    }

    &_blogsBlock#{&}_second#{&}_right#{&}_isMobileView {
      top: 550px;
      bottom: auto;
      left: 50%;
      right: auto;
      transform: translateX(-50%);
      border-radius: 26px;
      padding-left: 0;
      padding-right: 0;
      justify-content: center;
    }
  }

  &__stripe-text {
    margin-right: 8px;

    @include mediaMinMax(1025, $normal) {
      margin-right: vw-full(8);
    }
  }

  &__stripe-edit-icon {
    flex-shrink: 0;
  }
}
</style>
